import { useEffect, useState } from 'react';
import { Domain } from '../types/data';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { extractErrorMessageFromResponse } from '../services/errorService';
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from '@sentry/react';
import DomainSelector from '../components/shared/DomainSelector';
import { CustomerGroupSelector } from '../components/shared/CustomerGroupSelector';
import { aiService } from '../services/aiService';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import { ArticleUrlInput } from '../components/SocialMediaPosts/ArticleUrlInput';
import {
  AdDrafts,
  AdPlatformSelector,
  SavePromptDialog,
  PromptsDrawer,
} from '../components/AdGeneration';
import { StreamingModal, StreamingProgressItem } from '../components/shared/StreamingModal';
import RichTextEditor from '../components/shared/RichTextEditor';
import {
  AD_PLATFORMS,
  AdDraft,
  formatDraft,
  mapToCustomerGroups,
} from '../components/AdGeneration/utils/utils';
import { FeatureFlag } from '../utils/featureFlags';
import { Button } from '@mui/material';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';

const AdGeneration = () => {
  const {
    domains,
    targetCompany,
    customerTargetGroups,
    fetchRelatedDomainData,
    fetchDomains,
    targetCompanyDetails,
  } = useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const [articleUrl, setArticleUrl] = useState('');
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const [selectedAdPlatform, setSelectedAdPlatform] = useState(AD_PLATFORMS[0].value);
  const [isGenerating, setIsGenerating] = useState(false);
  const [adGenerationPrompt, setAdGenerationPrompt] = useState('');
  const [streamingDrafts, setStreamingDrafts] = useState<Record<number, string>>({
    0: '',
    1: '',
    2: '',
  });
  const [streamingStatus, setStreamingStatus] = useState<Record<number, string>>({
    0: 'waiting',
    1: 'waiting',
    2: 'waiting',
  });
  const [generatedDrafts, setGeneratedDrafts] = useState<AdDraft[]>([]);
  const [isSavePromptDialogOpen, setIsSavePromptDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    loadData();
  }, [domains, targetCompany]);

  const loadData = () => {
    if (targetCompany && domains.length > 0) {
      const foundDomain = domains.find((domain: Domain) => domain.company_id === targetCompany);
      if (foundDomain && typeof foundDomain !== 'undefined') {
        setActiveDomain(foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  };

  const generateSingleDraft = async (index: number) => {
    const trimmedUrl = articleUrl.trim();

    const { data: response, error: generateAdError } = await aiService.generateAd({
      articleUrl: trimmedUrl,
      prompt: adGenerationPrompt,
      draftIndex: index + 1,
      language: activeDomain?.language || 'English',
      toneOfVoice: activeDomain?.tone_of_voice || 'Professional',
      adPlatform: selectedAdPlatform,
      customerGroupDescription:
        customerTargetGroups.find((group) => group.name === selectedCustomerGroup)?.description ||
        '',
      companyId: targetCompany!,
      domainName: activeDomain!.domain!,
    });

    if (generateAdError) {
      setStreamingStatus((prev) => ({ ...prev, [index]: t('common.error') }));
      throw new Error(await extractErrorMessageFromResponse(generateAdError, t));
    }

    if (!response) {
      setStreamingStatus((prev) => ({ ...prev, [index]: t('common.error') }));
      throw new Error('No response received');
    }

    // Set streaming status to completed immediately as we're not streaming
    setStreamingStatus((prev) => ({ ...prev, [index]: t('common.completed') }));

    // Store the JSON string for reference
    const jsonString = JSON.stringify(response);
    setStreamingDrafts((prev) => ({
      ...prev,
      [index]: jsonString,
    }));

    return jsonString;
  };

  const handleSubmit = async () => {
    if (!articleUrl || !selectedCustomerGroup) {
      showNotification(t('common.please_fill_all_fields'), 'error');
      return;
    }

    setIsGenerating(true);
    setGeneratedDrafts([]);
    setStreamingDrafts({ 0: '', 1: '', 2: '' });
    setStreamingStatus({ 0: t('common.waiting'), 1: t('common.waiting'), 2: t('common.waiting') });

    try {
      const drafts = await Promise.all([
        generateSingleDraft(0),
        generateSingleDraft(1),
        generateSingleDraft(2),
      ]);
      showNotification(t('ads.success'), 'success');

      const formattedDrafts: AdDraft[] = drafts.map((content, index) => {
        return formatDraft(content, index, t, targetCompanyDetails?.name);
      });

      setGeneratedDrafts(formattedDrafts);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error generating ad drafts:', error);
      showNotification(
        error instanceof Error ? error.message : t('common.error_occurred'),
        'error'
      );

      // Update streaming status for all drafts to show error
      setStreamingStatus({
        0: t('common.error'),
        1: t('common.error'),
        2: t('common.error'),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyPrompt = (content: string) => {
    setAdGenerationPrompt(content);
  };

  if (!targetCompany || !activeDomain) {
    return (
      <StatusMessage
        targetCompany={targetCompany ? targetCompany : null}
        activeDomain={activeDomain ? activeDomain.domain : null}
      />
    );
  }

  return (
    <FeatureFlagOverlay feature={FeatureFlag.AD_GENERATION}>
      <div className="relative p-8">
        <ErrorBoundary>
          <h1 className="text-3xl font-bold mb-2">{t('ads.title')}</h1>
          <div className="mb-6 mt-4">
            <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
          </div>

          <div className="mb-6">
            <CustomerGroupSelector
              customerGroups={mapToCustomerGroups(customerTargetGroups || [])}
              selectedGroup={selectedCustomerGroup}
              onSelect={setSelectedCustomerGroup}
            />
          </div>

          <div className="mb-6">
            <AdPlatformSelector
              selectedPlatform={selectedAdPlatform}
              onSelect={(platform) => platform !== null && setSelectedAdPlatform(platform)}
            />
          </div>

          <ErrorBoundary>
            <div className="relative">
              <RichTextEditor
                label={t('common.custom_prompt')}
                value={adGenerationPrompt}
                onChange={setAdGenerationPrompt}
                savePrompt={
                  adGenerationPrompt.trim() ? () => setIsSavePromptDialogOpen(true) : undefined
                }
                simpleText={true}
              />
              <Button
                variant="outlined"
                className="absolute top-0 right-0"
                onClick={() => setIsDrawerOpen(true)}
                size="small"
                style={{ position: 'absolute', top: '-3px', textTransform: 'none' }}
              >
                {t('common.saved_prompts')}
              </Button>
            </div>
          </ErrorBoundary>

          <div className="mb-6">
            <ArticleUrlInput
              value={articleUrl}
              onChange={(url) => setArticleUrl(Array.isArray(url) ? url[0] || '' : url)}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={
              isGenerating ||
              !customerTargetGroups ||
              customerTargetGroups.length === 0 ||
              !selectedCustomerGroup
            }
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400"
          >
            {isGenerating ? t('common.generating') : t('ads.generate_ad_drafts')}
          </button>
          <ErrorBoundary>
            {isGenerating && (
              <StreamingModal
                title={t('common.generating_ads')}
                items={[0, 1, 2].map(
                  (index): StreamingProgressItem => ({
                    id: index,
                    title: `${t('ads.draft')} ${index + 1}`,
                    status: streamingStatus[index] || 'waiting',
                  })
                )}
                variant="compact"
              />
            )}
          </ErrorBoundary>
          <ErrorBoundary>
            {generatedDrafts.length > 0 && (
              <AdDrafts drafts={generatedDrafts} platform={selectedAdPlatform} />
            )}
          </ErrorBoundary>
        </ErrorBoundary>

        <SavePromptDialog
          isOpen={isSavePromptDialogOpen}
          onClose={() => setIsSavePromptDialogOpen(false)}
          promptContent={adGenerationPrompt}
          companyId={targetCompany}
          domainId={activeDomain.id}
          feature={FeatureFlag.AD_GENERATION}
        />

        <PromptsDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onApplyPrompt={handleApplyPrompt}
          companyId={targetCompany}
          domainId={activeDomain.id}
          feature={FeatureFlag.AD_GENERATION}
        />
      </div>
    </FeatureFlagOverlay>
  );
};

export default AdGeneration;
